"use client";

import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import type {
  INewsFilterProps,
  INewsFilterData,
} from "../../interfaces/NewsFilter.interface";
import FilterDropdownButton from "./components/FilterDropdownButton";
import FilterDropdownPanel from "./components/FilterDropdownPanel";
import FilterChip from "./components/FilterChip";
import { trackComponent } from "@repo/analytics";
import { getCustomerIdForAnalytics } from "../../utils/analytics-utils";
import {
  campaignName,
  journeyTag,
  productCategory,
  propertyType,
  referenceId,
  sprint,
  subSection,
} from "../../pages/News/newsTrackingConstants";

type FilterKey = "events" | "index" | "instrument" | "marketCap" | "sector";

interface FilterConfig {
  key: FilterKey;
  label: string;
  optionsKey: keyof INewsFilterData;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  dropdownWidth: number;
}

const NewsFilter: React.FC<INewsFilterProps> = ({
  filterOptions,
  selectedEvents,
  selectedIndex,
  selectedInstrument,
  selectedMarketCap,
  selectedSector,
  onEventsChange,
  onIndexChange,
  onInstrumentChange,
  onMarketCapChange,
  onSectorChange,
  onClearAll,
  aemContent,
}) => {
  const [openDropdown, setOpenDropdown] = useState<FilterKey | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<
    Record<FilterKey, React.RefObject<HTMLButtonElement | null>>
  >({
    events: React.createRef<HTMLButtonElement>(),
    index: React.createRef<HTMLButtonElement>(),
    instrument: React.createRef<HTMLButtonElement>(),
    marketCap: React.createRef<HTMLButtonElement>(),
    sector: React.createRef<HTMLButtonElement>(),
  });

  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        key: "events",
        label: aemContent?.filters?.events ?? "Events",
        optionsKey: "events",
        selectedValues: selectedEvents,
        onChange: onEventsChange,
        dropdownWidth: 180,
      },
      {
        key: "index",
        label: aemContent?.filters?.index ?? "Index",
        optionsKey: "index",
        selectedValues: selectedIndex,
        onChange: onIndexChange,
        dropdownWidth: 180,
      },
      {
        key: "instrument",
        label: aemContent?.filters?.instrumentType ?? "Instrument type",
        optionsKey: "instrument_type",
        selectedValues: selectedInstrument,
        onChange: onInstrumentChange,
        dropdownWidth: 200,
      },
      {
        key: "marketCap",
        label: aemContent?.filters?.marketCap ?? "Market cap",
        optionsKey: "market_cap",
        selectedValues: selectedMarketCap,
        onChange: onMarketCapChange,
        dropdownWidth: 180,
      },
      {
        key: "sector",
        label: aemContent?.filters?.sector ?? "Sector",
        optionsKey: "sector",
        selectedValues: selectedSector,
        onChange: onSectorChange,
        dropdownWidth: 180,
      },
    ],
    [
      aemContent,
      selectedEvents,
      selectedIndex,
      selectedInstrument,
      selectedMarketCap,
      selectedSector,
      onEventsChange,
      onIndexChange,
      onInstrumentChange,
      onMarketCapChange,
      onSectorChange,
    ],
  );

  const trackFilterBarEvent = useCallback(
    async ({
      clickType,
      ctaText,
      subSectionOverride,
    }: {
      clickType: "icon" | "checkbox" | "link";
      ctaText: string;
      subSectionOverride?: string;
    }) => {
      try {
        const customerId = await getCustomerIdForAnalytics();

        const eventTypeMap: Record<typeof clickType, string> = {
          icon: "filter_bar_selection_chips_chevron_icon_click",
          checkbox: "filter_bar_selection_chips_chevron_checkbox_click",
          link: "filter_bar_selection_chips_chevron_links_click",
        };

        await trackComponent({
          componentName: "filter_bar_selection_chips_chevron",
          sectionTitle: "latest",
          eventType: eventTypeMap[clickType],
          ctaText,
          productCategory,
          subSection,
          cleverTapOverrides: {
            EP_JOURNEY: journeyTag,
            EP_PROPERTY_TYPE: propertyType,
            EP_PRODUCT_NAME: subSection,
            EP_CAMPAIGN: campaignName,
            EP_SPRINT: sprint,
            EP_REFERENCE_ID: referenceId,
            ...(customerId && { EP_CUSTOMER_ID: customerId }),
            EP_SR_NUMBER: "1",
            EP_CUST_ATTRIBUTE_1: "filters",
            ...(subSectionOverride && { EP_SUB_SECTION: subSectionOverride }),
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track filter bar event", error);
      }
    },
    [],
  );

  // Keep scrollbar visible but freeze page scroll while a dropdown is open
  useEffect(() => {
    if (openDropdown === null) return;
    if (typeof window === "undefined") return;

    const lockedScrollY = window.scrollY;

    const handleScroll = () => {
      window.scrollTo(0, lockedScrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [openDropdown]);

  const updateDropdownPosition = useCallback(
    (
      buttonRef: React.RefObject<HTMLButtonElement | null>,
      dropdownWidth = 200,
    ) => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const padding = 8;

        // Clamp left so the dropdown doesn't overflow the right edge of the viewport
        let left = rect.left;
        if (left + dropdownWidth + padding > viewportWidth) {
          left = viewportWidth - dropdownWidth - padding;
        }
        // Also ensure it doesn't go off the left edge
        if (left < padding) {
          left = padding;
        }

        setDropdownPosition({
          top: rect.bottom + 8,
          left,
        });
      }
    },
    [],
  );

  const hasSelections =
    selectedEvents.length > 0 ||
    selectedIndex.length > 0 ||
    selectedInstrument.length > 0 ||
    selectedMarketCap.length > 0 ||
    selectedSector.length > 0;

  const toggleValue = useCallback(
    (
      selected: string[],
      value: string,
      onChange: (values: string[]) => void,
    ) => {
      onChange(
        selected.includes(value)
          ? selected.filter((v) => v !== value)
          : [...selected, value],
      );

      // Track checkbox selection
      trackFilterBarEvent({
        clickType: "checkbox",
        ctaText: value,
        subSectionOverride: "checkbox",
      });
    },
    [trackFilterBarEvent],
  );

  const handleDropdownToggle = useCallback(
    (filterKey: FilterKey, dropdownWidth: number, label: string) => {
      const shouldOpen = openDropdown !== filterKey;

      if (shouldOpen) {
        const buttonRef = buttonRefs.current[filterKey];
        if (buttonRef) {
          updateDropdownPosition(buttonRef, dropdownWidth);
        }
        setOpenDropdown(filterKey);
      } else {
        setOpenDropdown(null);
      }

      // Track chevron icon click (open/close)
      trackFilterBarEvent({
        clickType: "icon",
        ctaText: label,
        subSectionOverride: shouldOpen ? "open" : "close",
      });
    },
    [openDropdown, updateDropdownPosition, trackFilterBarEvent],
  );

  const closeAllDropdowns = useCallback(() => {
    setOpenDropdown(null);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".dropdown-wrapper") &&
        !target.closest(".dropdown-panel")
      ) {
        closeAllDropdowns();
      }
    };

    const scrollContainer = scrollContainerRef.current;

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", closeAllDropdowns);
    scrollContainer?.addEventListener("scroll", closeAllDropdowns);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", closeAllDropdowns);
      scrollContainer?.removeEventListener("scroll", closeAllDropdowns);
    };
  }, [closeAllDropdowns]);

  // Collect all selected chips for display
  const createChip = useCallback(
    (
      value: string,
      configKey: string,
      selectedValues: string[],
      onChange: (values: string[]) => void,
    ) => ({
      key: `${configKey}-${value}`,
      label: value,
      onRemove: () => toggleValue(selectedValues, value, onChange),
    }),
    [toggleValue],
  );

  const allSelectedChips = useMemo(
    () =>
      filterConfigs.flatMap((config) =>
        config.selectedValues.map((value) =>
          createChip(value, config.key, config.selectedValues, config.onChange),
        ),
      ),
    [filterConfigs, createChip],
  );

  const handleClearAllWithTracking = useCallback(() => {
    trackFilterBarEvent({
      clickType: "link",
      ctaText: aemContent?.clearFiltersLabel ?? "Clear filters",
      subSectionOverride: "clear filters",
    });
    onClearAll();
  }, [aemContent?.clearFiltersLabel, onClearAll, trackFilterBarEvent]);

  return (
    <>
      <div className={`px-4 ${hasSelections ? "pb-2 pt-5" : "py-4"}`}>
        <div
          ref={scrollContainerRef}
          className={`relative flex gap-3 overflow-x-auto scroll-smooth ${
            hasSelections ? "mb-4" : ""
          } [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
        >
          {filterConfigs.map((config) => (
            <FilterDropdownButton
              key={config.key}
              label={config.label}
              isOpen={openDropdown === config.key}
              buttonRef={buttonRefs.current[config.key]}
              onClick={() =>
                handleDropdownToggle(
                  config.key,
                  config.dropdownWidth,
                  config.label,
                )
              }
            />
          ))}
        </div>

        {/* Dropdown panels rendered outside the scroll container with fixed positioning */}
        {filterConfigs.map((config) => (
          <FilterDropdownPanel
            key={config.key}
            isOpen={openDropdown === config.key}
            options={filterOptions?.[config.optionsKey] ?? []}
            selectedValues={config.selectedValues}
            onToggle={(value) =>
              toggleValue(config.selectedValues, value, config.onChange)
            }
            position={dropdownPosition}
            width={config.dropdownWidth}
          />
        ))}

        {hasSelections && (
          <>
            <div className="-mx-5 mb-2 mt-2">
              <div className="h-px w-full bg-gray-200"></div>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {allSelectedChips.map((chip) => (
                  <FilterChip
                    key={chip.key}
                    label={chip.label}
                    onRemove={chip.onRemove}
                  />
                ))}
              </div>
              <div className="flex gap-3">
                <div className="bg-lgray-200 inline-block h-[46px] w-px" />
                <button
                  type="button"
                  onClick={handleClearAllWithTracking}
                  className="font-rubik text-link-primary whitespace-nowrap text-[14px] font-medium leading-none"
                >
                  {aemContent?.clearFiltersLabel ?? "Clear filters"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="h-px w-full bg-gray-200"></div>
    </>
  );
};

export default NewsFilter;
